// ---------------------------------------------------------------------------
// MainTariffStep — Shared for dso_main_tariff + retailer_main_tariff
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import { OptionCard } from '@/components/shared/OptionCard';
import { PropertyFilters, filterByProperties, getFilterableKeys } from '@/components/shared/PropertyFilters';
import { StepHeading } from '@/components/shared/StepHeading';
import { CoverageNudge } from '@/components/shared/CoverageNudge';
import { t } from '@/i18n';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import { getStepIcon } from '@/utils/stepIcons';
import styles from './MainTariffStep.module.css';

export const MainTariffStep = memo(function MainTariffStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const collectedData = useCollectedData();
  const previousMainTariffId = useMemo(() => {
    const group = stepId.startsWith('dso') ? 'dso' : 'retailer';
    const nested = collectedData[group];
    if (typeof nested === 'object' && nested !== null) {
      const id = (nested as Record<string, unknown>)['main_tariff_id'];
      return typeof id === 'string' || typeof id === 'number' ? String(id) : '';
    }
    return '';
  }, [collectedData, stepId]);

  const handleFilterChange = useCallback((key: string, valueId: string) => {
    setActiveFilters((prev) => {
      if (prev[key] === valueId) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: valueId };
    });
  }, []);

  const { regularOptions, customOption } = useMemo(() => {
    const regular = options.options.filter((o) => o.metadata['is_custom'] !== true);
    const custom = options.options.find((o) => o.metadata['is_custom'] === true);
    return { regularOptions: regular, customOption: custom ?? null };
  }, [options.options]);

  const filterableKeys = useMemo(() => getFilterableKeys(regularOptions), [regularOptions]);

  const filteredOptions = useMemo(
    () => filterByProperties(regularOptions, activeFilters),
    [regularOptions, activeFilters],
  );

  const handleSelect = useCallback(
    (id: string) => {
      if (isSubmitting) return;
      onSubmit({ main_tariff_id: id });
    },
    [onSubmit, isSubmitting],
  );

  const titleKey = `steps.${stepId}.title`;
  const subtitleKey = `steps.${stepId}.subtitle`;

  const hasListed = regularOptions.length > 0;
  const customLabel = hasListed
    ? t('steps.common.tariffNotListed')
    : t('steps.common.createTariff');

  return (
    <>
      <StepHeading title={t(titleKey)} subtitle={t(subtitleKey)} icon={getStepIcon(stepId)} />
      {hasListed && (
        <PropertyFilters
          options={regularOptions}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />
      )}
      {hasListed ? (
        <div className={styles['list']} role="listbox" aria-label={t(titleKey)}>
          {filteredOptions.map((o) => (
            <OptionCard
              key={o.id}
              id={o.id}
              name={o.name}
              badges={(o.metadata['properties'] as Array<{ key: string; value_name: string }> | undefined)
                ?.filter((p) => filterableKeys.has(p.key))
                .map((p) => p.value_name)}
              isSelected={o.id === previousMainTariffId}
              onClick={handleSelect}
            />
          ))}
        </div>
      ) : (
        customOption && (
          <p className={styles['emptyState']}>{t('steps.common.noTariffsFound')}</p>
        )
      )}
      <CoverageNudge coverage={options._coverage} />
      {customOption && (
        <button
          type="button"
          className={styles['customCta']}
          onClick={() => handleSelect(customOption.id)}
          disabled={isSubmitting}
        >
          {customLabel} <span aria-hidden="true">→</span>
        </button>
      )}
    </>
  );
});
